import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FeatherPlus } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { DateRangeFilter } from "../../../../shared/presentation/components/DateRangeFilter";
import {
  DateRangeValue,
  formatDateForApi,
} from "../../../../shared/presentation/utils/dateRangeFilters";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedSort } from "../../../../shared/presentation/hooks/usePersistedSort";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import { DebitNoteEntity, DebitNoteListStatus } from "../../domain";
import { useDebitNoteCounts } from "../hooks/useDebitNoteCounts";
import { useDebitNoteList } from "../hooks/useDebitNoteList";
import { DebitNoteListToolbar } from "./DebitNoteListToolbar";
import { DebitNoteStatsCards } from "./DebitNoteStatsCards";
import { DebitNoteTable } from "./DebitNoteTable";

const ITEMS_PER_PAGE = 10;
const DEBIT_NOTE_LIST_STATUSES: DebitNoteListStatus[] = [
  "all",
  "pending",
  "under_review",
  "posted",
  "rejected",
];
type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeDebitNoteStatus = (
  raw: string,
  fallback: DebitNoteListStatus,
) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    DEBIT_NOTE_LIST_STATUSES.includes(parsed as DebitNoteListStatus)
    ? (parsed as DebitNoteListStatus)
    : fallback;
};

const DebitNotesSection: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = usePersistedState<DebitNoteListStatus>(
    "debitNotes.list.status",
    "all",
    { deserialize: deserializeDebitNoteStatus },
  );
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = usePersistedDateRange(
    "debitNotes.list.dateRange",
  );
  const [sort, setSort] = usePersistedSort("debitNotes.list", {
    by: "created_at",
    order: "desc",
    label: "Created Date: Newest First",
  });
  const [listLoadingReason, setListLoadingReason] =
    useState<ListLoadingReason>("initial");
  const listLoadingStartedRef = useRef(false);

  const { debitNotes, pagination, loading, error, fetchDebitNotes } =
    useDebitNoteList();

  const countParams = useMemo(
    () => ({
      date_from:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      date_to:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [dateRange.from, dateRange.preset, dateRange.to],
  );
  const {
    counts: debitNoteCounts,
    loading: countsLoading,
    fetchCounts,
  } = useDebitNoteCounts(countParams);

  const listParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      status,
      search: searchTerm,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
      sortBy: sort.by as "created_at" | "updated_at",
      sortOrder: sort.order as "asc" | "desc",
    }),
    [
      dateRange.from,
      dateRange.preset,
      dateRange.to,
      page,
      searchTerm,
      sort.by,
      sort.order,
      status,
    ],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => {
        fetchDebitNotes(listParams);
      },
      searchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [fetchDebitNotes, listParams, searchTerm]);

  useEffect(() => {
    if (loading) {
      listLoadingStartedRef.current = true;
      return;
    }

    if (listLoadingStartedRef.current) {
      listLoadingStartedRef.current = false;
      setListLoadingReason(null);
    }
  }, [loading]);

  const handleStatusChange = (nextStatus: DebitNoteListStatus) => {
    if (nextStatus === status) return;

    setListLoadingReason("refresh");
    setStatus(nextStatus);
    setPage(1);
  };

  const handleDateRangeChange = (nextDateRange: DateRangeValue) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setDateRange(nextDateRange);
    setPage(1);
  };

  const handleSearchTermChange = (value: string) => {
    if (value === searchTerm) return;

    setListLoadingReason("refresh");
    setSearchTerm(value);
    setPage(1);
  };

  const handleSortChange = (
    sortBy: string,
    sortOrder: string,
    label: string,
  ) => {
    if (sortBy === sort.by && sortOrder === sort.order && label === sort.label) {
      return;
    }

    setListLoadingReason("refresh");
    setSort({ by: sortBy, order: sortOrder, label });
    setPage(1);
  };

  const handleRefresh = useCallback(() => {
    setListLoadingReason("refresh");
    fetchDebitNotes(listParams);
    fetchCounts(countParams);
  }, [countParams, fetchCounts, fetchDebitNotes, listParams]);

  const handlePageChange = (nextPage: number) => {
    if (nextPage === page) return;

    setListLoadingReason("pagination");
    setPage(nextPage);
  };

  const handleCreateManual = () => {
    navigate("/debit/review/new", {
      state: { isManualCreation: true },
    });
  };

  const handleReviewClick = (document: DebitNoteEntity) => {
    navigate(`/debit/review/${document.id}`, {
      state: {
        debitNoteJson: document.json,
        documentId: document.id.toString(),
        debitNoteDocument: document,
        signedUrl: document.signed_url,
        isEdit:
          document.status === "1" || String(document.is_under_review) === "1",
        jobId: document.jobid,
      },
    });
  };

  const showTableSkeleton =
    (listLoadingReason === "initial" && !pagination) ||
    listLoadingReason === "pagination" ||
    (loading && !pagination);
  const showInlineLoading =
    !showTableSkeleton && (listLoadingReason === "refresh" || loading);
  const smoothTableSkeleton = useMinimumLoading(showTableSkeleton);
  const smoothInlineLoading = useMinimumLoading(showInlineLoading);

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 pt-8 pb-40">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Debit Notes
          </span>
          <span className="text-body font-body text-subtext-color">
            Track and manage your debit note workflow
          </span>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={handleCreateManual}
        >
          Create Debit Note
        </Button>
      </div>

      <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />

      <DebitNoteStatsCards
        counts={debitNoteCounts}
        loading={countsLoading}
        onSelectStatus={handleStatusChange}
      />

      <DebitNoteListToolbar
        status={status}
        onStatusChange={handleStatusChange}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        sort={sort}
        onSortChange={handleSortChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <DebitNoteTable
        debitNotes={debitNotes}
        loading={smoothTableSkeleton}
        inlineLoading={smoothInlineLoading}
        error={error}
        pagination={pagination}
        itemsPerPage={ITEMS_PER_PAGE}
        searchTerm={searchTerm}
        onReview={handleReviewClick}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default DebitNotesSection;
